import { useEffect, useLayoutEffect, useReducer, useRef, useState, lazy, Suspense } from 'react'
import type { Socket } from 'socket.io-client'
import type { Attachment, BotOption, Message, PrivacyNoticeConfig } from './types.js'
import { widgetReducer, initialWidgetState, type WidgetState } from './state-machine.js'
import { AgentsBusy } from './AgentsBusy.js'
import { RatingPopup } from './RatingPopup.js'
import { Attachments } from './components/Attachments.js'
import { PaperclipIcon, SendIcon, SmileIcon } from './components/Icons.js'

const EmojiPicker = lazy(() =>
  import('@emoji-mart/react').then((m) => ({ default: m.default as React.ComponentType<EmojiPickerProps> })),
)

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- @emoji-mart/react has no bundled TypeScript types; props interface is hand-written here
type EmojiPickerProps = { data: unknown; onEmojiSelect: (e: { native: string }) => void }

function attachmentKeys(attachments?: Attachment[]): string {
  return JSON.stringify((attachments ?? []).map((a) => a.attachmentId).sort())
}

function findLastPendingMatch(messages: Message[], candidate: Message): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (!m) continue
    if (
      m.from === 'visitor' &&
      // Match both 'pending' and 'sent' — the server ack may arrive
      // before the RabbitMQ round-trip delivers widget:message:new back
      // to the sender, in which case the optimistic message's status has
      // already flipped from 'pending' to 'sent'. Without matching 'sent'
      // too, the echo would be appended as a duplicate.
      (m.status === 'pending' || m.status === 'sent') &&
      m.text === candidate.text &&
      attachmentKeys(m.attachments) === attachmentKeys(candidate.attachments) &&
      Math.abs(new Date(m.createdAt).getTime() - new Date(candidate.createdAt).getTime()) < 60_000
    ) {
      return i
    }
  }
  return -1
}

/** A cached thread is dropped after this long without a new message (SW-06). */
export const MESSAGE_CACHE_TTL_MS = 24 * 60 * 60 * 1000

/**
 * Reads the cached thread. Accepts the current `{ savedAt, messages }` shape and
 * the legacy bare array (kept readable; it is re-stamped on the next write).
 * An expired entry is removed and treated as empty.
 */
export function readCachedMessages(key: string): Message[] {
  const saved = localStorage.getItem(key)
  if (!saved) return []
  const parsed = JSON.parse(saved) as Message[] | { savedAt?: number; messages?: Message[] }
  if (Array.isArray(parsed)) return parsed
  if (typeof parsed.savedAt === 'number' && Date.now() - parsed.savedAt > MESSAGE_CACHE_TTL_MS) {
    localStorage.removeItem(key)
    return []
  }
  return parsed.messages ?? []
}

/**
 * SW-06: only what is needed to redraw the thread is written to the browser.
 * Delivery/read status and per-message ratings are server-side and refetched.
 */
export function minimiseMessages(messages: Message[]): Message[] {
  return messages.map((m) => ({
    id: m.id,
    from: m.from,
    createdAt: m.createdAt,
    ...(m.text !== undefined && { text: m.text }),
    ...(m.attachments !== undefined && { attachments: m.attachments }),
    ...(m.ai !== undefined && { ai: m.ai }),
  }))
}

/**
 * SW-06: removes what the widget keeps in the browser for this visitor.
 * Called when a conversation ends and (with includeConsent) when the visitor
 * withdraws consent, which also forgets the local "notice accepted" flag.
 */
export function clearWidgetStorage(visitorId: string, opts: { includeConsent?: boolean } = {}): void {
  try {
    localStorage.removeItem(`kc3-widget-msgs:${visitorId}`)
    localStorage.removeItem(`kc3-widget-conversation:${visitorId}`)
    if (opts.includeConsent) {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i)
        if (k && k.startsWith(`kc3-widget-privacy:${visitorId}:`)) localStorage.removeItem(k)
      }
    }
  } catch {
    // ignore storage errors (e.g. private mode)
  }
}

function isReassignmentNotice(text?: string): boolean {
  return text != null && /^Reassigned to .+ \(previous agent went offline\)$/.test(text)
}

function isSystemNotice(text?: string): boolean {
  return isReassignmentNotice(text) || (text != null && /^Chat (resumed|restarted)/i.test(text))
}

export function Widget({
  socket,
  visitorId,
  token,
  conversationId,
  initialMessage,
  onRestart,
  persistHistory = true,
  readOnly = false,
  privacyNotice,
}: {
  socket: Socket
  visitorId: string
  token: string
  /** PV-16: when set, the visitor must accept this notice before sending a message. */
  privacyNotice?: PrivacyNoticeConfig
  conversationId?: string
  initialMessage?: ConversationMessageNewPayload
  onRestart?: () => void
  /**
   * When true (default, backward-compatible for anonymous/guest visitors),
   * the widget caches messages in localStorage so a page refresh restores
   * the thread without a server round-trip.
   *
   * When false (logged-in clients), messages are fetched from the database
   * via the `widget:history:request` Socket.IO event on mount and never
   * written to localStorage — the database is the single source of truth,
   * so a returning user sees the same history on any device/browser.
   */
  persistHistory?: boolean
  readOnly?: boolean
}) {
  const storageKey = `kc3-widget-msgs:${visitorId}`
  const [messages, setMessages] = useState<Message[]>(() => {
    if (!persistHistory) return []
    try {
      return readCachedMessages(storageKey)
    } catch {
      return []
    }
  })
  const [text, setText] = useState('')
  const [showEmoji, setShowEmoji] = useState(false)
  const [isAgentTyping, setIsAgentTyping] = useState(false)
  const [typingName, setTypingName] = useState<string | null>(null)
  const typingClearRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [pendingAttachment, setPendingAttachment] = useState<Attachment | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const bottomRef = useRef<HTMLLIElement>(null)
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingHistoryMode = useRef<'initial' | 'older'>('initial')
  const oldestCreatedAtRef = useRef<string | null>(null)
  const shouldScrollToBottomRef = useRef(false)
  const conversationIdRef = useRef(conversationId)
  const messagesRef = useRef<Message[]>(messages)
  const deliveredAckedRef = useRef<Set<string>>(new Set())
  const readAckedRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    conversationIdRef.current = conversationId
  }, [conversationId])

  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  const [isSending, setIsSending] = useState(false)
  const sendingRef = useRef(false)
  // Mirrors the "Connecting you to a live agent…" bubble's own gate
  // (conversationId === undefined) below — a visitor queued but not yet
  // matched to an agent has no one to receive a message, so the composer
  // stays disabled until conversationId is set (AGENT_JOINED).
  const noticeKey = privacyNotice ? `kc3-widget-privacy:${visitorId}:${privacyNotice.version}` : null
  const [noticeAccepted, setNoticeAccepted] = useState<boolean>(() => {
    if (!noticeKey) return true
    try {
      return localStorage.getItem(noticeKey) === '1'
    } catch {
      return false
    }
  })
  const [noticeBusy, setNoticeBusy] = useState(false)
  // SW-RM8: when the tenant sets a minimum age the visitor must confirm it before the chat opens.
  const [ageConfirmed, setAgeConfirmed] = useState(false)
  const [noticeError, setNoticeError] = useState<string | null>(null)
  const noticeGate = !!privacyNotice && !noticeAccepted
  const canSendMessage = conversationId !== undefined && !readOnly && !noticeGate

  async function acceptNotice() {
    if (!privacyNotice || noticeBusy) return
    if (privacyNotice.minimumAge && !ageConfirmed) return
    setNoticeBusy(true)
    setNoticeError(null)
    try {
      const resp = await fetch('/widget/v1/privacy-acceptance', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: privacyNotice.version }),
      })
      if (!resp.ok) {
        setNoticeError('We could not record this. Please try again.')
        return
      }
      try {
        if (noticeKey) localStorage.setItem(noticeKey, '1')
      } catch {
        // acceptance is recorded server-side; the local flag only avoids re-asking
      }
      setNoticeAccepted(true)
    } catch {
      setNoticeError('We could not record this. Please try again.')
    } finally {
      setNoticeBusy(false)
    }
  }

  function handleScroll(e: React.UIEvent<HTMLDivElement>) {
    if (!hasMoreMessages || isLoadingMore || !oldestCreatedAtRef.current || conversationId === undefined) return
    const el = e.currentTarget
    if (el.scrollTop <= el.clientHeight * 0.2) {
      pendingHistoryMode.current = 'older'
      setIsLoadingMore(true)
      socket.emit('widget:history:request', {
        conversationId,
        before: oldestCreatedAtRef.current,
        limit: 20,
      })
    }
  }

  useEffect(() => {
    const onNew = (e: ConversationMessageNewPayload) => {
      const from = e.direction === 'outbound' ? 'agent' : 'visitor'
      const msg: Message = {
        id: e.messageId,
        from,
        text: e.body ?? e.lastMessagePreview,
        ...(e.attachments?.length && {
          attachments: e.attachments.map((a) => ({
            attachmentId: a.id,
            filename: a.filename,
            contentType: a.mimeType,
            size: a.sizeBytes,
          })),
        }),
        status: 'sent',
        createdAt: e.lastMessageAt,
        ...(e.aiGenerated === true && { ai: true }),
      }
      if (from === 'agent' && e.conversationId) {
        if (!deliveredAckedRef.current.has(e.messageId)) {
          deliveredAckedRef.current.add(e.messageId)
          socket.emit('widget:message:ack', { conversationId: e.conversationId, messageId: e.messageId, status: 'delivered' })
        }
        if (!document.hidden && !readAckedRef.current.has(e.messageId)) {
          readAckedRef.current.add(e.messageId)
          socket.emit('widget:message:ack', { conversationId: e.conversationId, messageId: e.messageId, status: 'read' })
        }
      }
      shouldScrollToBottomRef.current = true
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev
        if (from === 'visitor') {
          const index = findLastPendingMatch(prev, msg)
          if (index !== -1) {
            const next = [...prev]
            next[index] = msg
            return next
          }
        }
        return [...prev, msg]
      })
    }
    const onDelivered = ({ id }: { id: string }) =>
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'delivered' as const } : m)))
    const onRead = ({ id }: { id: string }) =>
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'read' as const } : m)))
    const onBotReply = ({ message }: { message: string }) => {
      shouldScrollToBottomRef.current = true
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), from: 'bot', text: message, createdAt: new Date().toISOString() },
      ])
    }
    // Never let a lost "stop" leave the dots up forever: clear after 30 s, and
    // also as soon as a reply lands (see onBotReply / new-message handlers).
    const onAgentTypingStart = (payload?: { actorName?: string }) => {
      setTypingName(payload?.actorName ?? null)
      setIsAgentTyping(true)
      if (typingClearRef.current) clearTimeout(typingClearRef.current)
      typingClearRef.current = setTimeout(() => setIsAgentTyping(false), 30_000)
    }
    const onAgentTypingStop = () => {
      if (typingClearRef.current) clearTimeout(typingClearRef.current)
      setIsAgentTyping(false)
    }
    const onReconnect = () => {
      const cid = conversationIdRef.current
      if (!cid) {
        return
      }
      pendingHistoryMode.current = 'initial'
      setIsLoadingMore(true)
      socket.emit('widget:history:request', { conversationId: cid, limit: 20 })
    }
    const onHistory = (payload: {
      messages: {
        id: string
        direction: 'inbound' | 'outbound'
        body: string
        aiGenerated?: boolean
        attachments?: { id: string; filename: string; mimeType: string; sizeBytes: number }[]
        createdAt: string
      }[]
      hasMore: boolean
      before?: string
    }) => {
      setIsLoadingMore(false)
      setIsLoadingMore(false)
      const history = payload?.messages ?? []
      const msgs: Message[] = history.map((m) => ({
        id: m.id,
        from: m.direction === 'outbound' ? 'agent' : 'visitor',
        text: m.body,
        ...(m.attachments?.length && {
          attachments: m.attachments.map((a) => ({
            attachmentId: a.id,
            filename: a.filename,
            contentType: a.mimeType,
            size: a.sizeBytes,
          })),
        }),
        status: 'sent' as const,
        createdAt: m.createdAt,
        ...(m.aiGenerated === true && { ai: true }),
      }))
      setMessages((prev) => {
        if (pendingHistoryMode.current === 'older') {
          // prepend older messages, deduplicate by id
          const combined = [...msgs, ...prev]
          const seen = new Set<string>()
          const next: Message[] = []
          for (const m of combined) {
            if (seen.has(m.id)) continue
            seen.add(m.id)
            next.push(m)
          }
          return next
        }
        // initial: replace with server history, then merge any newer local messages
        const newestIncoming = msgs[msgs.length - 1]?.createdAt
        const next: Message[] = [...msgs]
        const seen = new Set<string>(msgs.map((m) => m.id))
        for (const m of prev) {
          if (seen.has(m.id)) continue
          if (newestIncoming && m.createdAt <= newestIncoming) continue
          next.push(m)
          seen.add(m.id)
        }
        return next
      })
      setHasMoreMessages(payload.hasMore)
      oldestCreatedAtRef.current = payload.before ?? msgs[0]?.createdAt ?? null
      if (pendingHistoryMode.current === 'initial') {
        shouldScrollToBottomRef.current = true
        const cid = conversationIdRef.current
        if (cid) {
          for (const m of msgs) {
            if (m.from !== 'agent') continue
            if (!deliveredAckedRef.current.has(m.id)) {
              deliveredAckedRef.current.add(m.id)
              socket.emit('widget:message:ack', { conversationId: cid, messageId: m.id, status: 'delivered' })
            }
            if (!document.hidden && !readAckedRef.current.has(m.id)) {
              readAckedRef.current.add(m.id)
              socket.emit('widget:message:ack', { conversationId: cid, messageId: m.id, status: 'read' })
            }
          }
        }
      }
    }

    socket.on('widget:message:new', onNew)
    socket.on('widget:message:delivered', onDelivered)
    socket.on('widget:message:read', onRead)
    socket.on('widget:bot:reply', onBotReply)
    socket.on('widget:typing:start', onAgentTypingStart)
    socket.on('widget:typing:stop', onAgentTypingStop)
    socket.on('widget:history:response', onHistory)
    socket.on('reconnect', onReconnect)

    return () => {
      socket.off('widget:message:new', onNew)
      socket.off('widget:message:delivered', onDelivered)
      socket.off('widget:message:read', onRead)
      socket.off('widget:bot:reply', onBotReply)
      socket.off('widget:typing:start', onAgentTypingStart)
      socket.off('widget:typing:stop', onAgentTypingStop)
      socket.off('widget:history:response', onHistory)
      socket.off('reconnect', onReconnect)
    }
  }, [socket])

  useEffect(() => {
    if (shouldScrollToBottomRef.current) {
      shouldScrollToBottomRef.current = false
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  useEffect(() => {
    if (!persistHistory) return
    try {
      // SW-06: nothing is kept for an empty or ended conversation.
      if (readOnly || messages.length === 0) {
        localStorage.removeItem(storageKey)
        return
      }
      localStorage.setItem(storageKey, JSON.stringify({ savedAt: Date.now(), messages: minimiseMessages(messages) }))
    } catch {
      // ignore storage errors (e.g. private mode)
    }
  }, [messages, storageKey, persistHistory, readOnly])

  // SW-06: host page can withdraw consent with window.dispatchEvent(new CustomEvent('kc3:withdraw-consent')).
  useEffect(() => {
    const onWithdraw = () => {
      clearWidgetStorage(visitorId, { includeConsent: true })
      setMessages([])
      setNoticeAccepted(!privacyNotice)
    }
    window.addEventListener('kc3:withdraw-consent', onWithdraw)
    return () => window.removeEventListener('kc3:withdraw-consent', onWithdraw)
  }, [visitorId, privacyNotice])

  // If the visitor switches back to the tab, mark any agent messages that
  // have not been acked as read so the agent sees the read receipt.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) return
      const cid = conversationIdRef.current
      if (!cid) return
      const agentMessages = messagesRef.current.filter((m) => m.from === 'agent' && m.status !== 'read')
      for (const m of agentMessages) {
        if (readAckedRef.current.has(m.id)) continue
        readAckedRef.current.add(m.id)
        socket.emit('widget:message:ack', { conversationId: cid, messageId: m.id, status: 'read' })
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [socket])

  // Request the most-recent 20 messages when a live chat conversation is assigned.
  useEffect(() => {
    if (conversationId !== undefined) {
      pendingHistoryMode.current = 'initial'
      setIsLoadingMore(true)
      socket.emit('widget:history:request', { conversationId, limit: 20 })
    }
  }, [conversationId, socket])

  // Append any message that arrived before the Widget could mount/listen.
  useEffect(() => {
    if (!initialMessage) return
    const from = initialMessage.direction === 'outbound' ? 'agent' : 'visitor'
    const msg: Message = {
      id: initialMessage.messageId,
      from,
      text: initialMessage.body ?? initialMessage.lastMessagePreview,
      ...(initialMessage.attachments?.length && {
        attachments: initialMessage.attachments.map((a) => ({
          attachmentId: a.id,
          filename: a.filename,
          contentType: a.mimeType,
          size: a.sizeBytes,
        })),
      }),
      status: 'sent' as const,
      createdAt: initialMessage.lastMessageAt,
      ...(initialMessage.aiGenerated === true && { ai: true }),
    }
    if (from === 'agent') {
      const cid = initialMessage.conversationId ?? conversationId
      if (cid) {
        if (!deliveredAckedRef.current.has(msg.id)) {
          deliveredAckedRef.current.add(msg.id)
          socket.emit('widget:message:ack', { conversationId: cid, messageId: msg.id, status: 'delivered' })
        }
        if (!document.hidden && !readAckedRef.current.has(msg.id)) {
          readAckedRef.current.add(msg.id)
          socket.emit('widget:message:ack', { conversationId: cid, messageId: msg.id, status: 'read' })
        }
      }
    }
    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]))
  }, [initialMessage])

  // Clear visitor typing indicator when leaving/unmounting.
  useEffect(() => {
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
      const payload = conversationId !== undefined ? { conversationId } : {}
      socket.emit('widget:typing:stop', payload)
    }
  }, [conversationId, socket])

  function send() {
    const t = text.trim()
    if (sendingRef.current || isSending || !canSendMessage || (!t && !pendingAttachment)) return
    sendingRef.current = true
    setIsSending(true)
    const id = crypto.randomUUID()
    const typingPayload = conversationId !== undefined ? { conversationId } : {}
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
    socket.emit('widget:typing:stop', typingPayload)
    const attachmentPayload = pendingAttachment
      ? {
          attachmentId: pendingAttachment.attachmentId,
          filename: pendingAttachment.filename,
          mimeType: pendingAttachment.contentType,
          sizeBytes: pendingAttachment.size,
        }
      : undefined
    const payload = {
      content: t,
      ...(attachmentPayload && { attachmentIds: [attachmentPayload.attachmentId], attachments: [attachmentPayload] }),
      ...(conversationId !== undefined && { conversationId }),
    }
    socket.emit('widget:message:send', payload, (result: unknown) => {
      if (result && typeof (result as { ok?: unknown }).ok === 'boolean' && (result as { ok: boolean }).ok) {
        setMessages((prev) =>
          prev.map((m) => (m.id === id ? { ...m, status: 'sent' as const } : m)),
        )
      }
    })
    const newMessage: Message = {
      id,
      from: 'visitor',
      // Must match the server's bodyText for attachment-only messages —
      // the server uses '[media]' (apps/ws-gateway/src/server.ts), so the
      // optimistic message must use the same text or findLastPendingMatch
      // will fail to reconcile the echo and show a duplicate.
      text: t || (pendingAttachment ? '[media]' : ''),
      ...(pendingAttachment && { attachments: [pendingAttachment] }),
      createdAt: new Date().toISOString(),
      status: 'pending',
    }
    setMessages((prev) => (prev.some((m) => m.id === newMessage.id) ? prev : [...prev, newMessage]))
    setText('')
    setPendingAttachment(null)
    setShowEmoji(false)
    setTimeout(() => {
      sendingRef.current = false
      setIsSending(false)
    }, 300)
  }

  async function uploadFile(file: File) {
    setIsUploading(true)
    setUploadError(null)
    const url = `/widget/v1/upload?visitorId=${encodeURIComponent(visitorId)}`
    const form = new FormData()
    form.append('file', file)
    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      })
      if (!resp.ok) {
        const text = await resp.text().catch(() => '')
        console.error('[kc3 widget] upload failed:', resp.status)
        setUploadError('Media failed to upload. Please try again.')
        return
      }
      const { attachmentId } = (await resp.json()) as { attachmentId: string }
      setPendingAttachment({
        attachmentId,
        filename: file.name,
        contentType: file.type,
        size: file.size,
      })
    } catch (err) {
      console.error('[kc3 widget] upload error:', err)
      setUploadError('Media failed to upload. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.repeat && !e.nativeEvent.isComposing) {
      e.preventDefault()
      send()
    }
  }

  function rateMessage(messageId: string, rating: 'up' | 'down') {
    const newRating = messages.find((m) => m.id === messageId)?.rating === rating ? undefined : rating
    socket.emit('widget:message:rate', { messageId, rating: newRating, conversationId })
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, rating: newRating } : m))
    )
  }

  return (
    <div className="kc3-widget">
      <div className="kc3-widget-header">
        <div className="kc3-widget-header__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>
        <div className="kc3-widget-header__content">
          <span className="kc3-widget-header__title">Live Agent</span>
          <span className="kc3-widget-header__subtitle">
            <span className="kc3-widget-header__online" />
            Online
          </span>
        </div>
      </div>

      <div className="kc3-messages-scroll" onScroll={handleScroll}>
        {isLoadingMore && (
          <div className="kc3-history-loader" aria-live="polite">
            <span className="kc3-history-loader__spinner" aria-hidden="true" />
            Loading older messages…
          </div>
        )}
        <div className="kc3-messages-divider">
          <span className="kc3-messages-divider__line" />
          <span className="kc3-messages-divider__text">Today</span>
          <span className="kc3-messages-divider__line" />
        </div>

        <ul aria-label="messages" aria-live="polite" aria-relevant="additions" className="kc3-messages">
          {messages.length === 0 && conversationId === undefined && (
            <li className="kc3-message kc3-message--agent kc3-message--welcome">
              <div className="kc3-message__avatar" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span className="kc3-message__avatar-status" />
              </div>
              <div className="kc3-message__bubble">
                <span className="kc3-message__text">Hi there! 👋\nHow can I help you today?</span>
              </div>
            </li>
          )}
          {messages.map((m, i) => {
            if (isSystemNotice(m.text)) {
              return (
                <li
                  key={m.id}
                  className="kc3-messages-divider kc3-messages-divider--reassign"
                  ref={i === messages.length - 1 ? bottomRef : null}
                >
                  <span className="kc3-messages-divider__line" />
                  <span className="kc3-messages-divider__text">{m.text}</span>
                  <span className="kc3-messages-divider__line" />
                </li>
              )
            }
            return (
              <li key={m.id} data-from={m.from} className={`kc3-message kc3-message--${m.from}`} ref={i === messages.length - 1 ? bottomRef : null}>
                {m.from === 'agent' && (
                  <div className="kc3-message__avatar" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    <span className="kc3-message__avatar-status" />
                  </div>
                )}
                <div className="kc3-message__bubble">
                  {m.from === 'agent' && m.ai === true && <span className="kc3-message__ai-badge">AI assistant</span>}
                  {m.text != null && <span className="kc3-message__text">{m.text}</span>}
                  {m.attachments != null && m.attachments.length > 0 && (
                    <Attachments attachments={m.attachments} token={token} />
                  )}
                  {m.from === 'visitor' && m.status != null && (
                    <span className="kc3-message__status">
                      {m.status}
                    </span>
                  )}
                  {m.from === 'agent' && (
                    <div className="kc3-message__rating">
                      <button
                        type="button"
                        aria-label="Helpful"
                        aria-pressed={m.rating === 'up'}
                        onClick={() => rateMessage(m.id, 'up')}
                        className="kc3-message__rate kc3-message__rate--up"
                      >
                        👍
                      </button>
                      <button
                        type="button"
                        aria-label="Not helpful"
                        aria-pressed={m.rating === 'down'}
                        onClick={() => rateMessage(m.id, 'down')}
                        className="kc3-message__rate kc3-message__rate--down"
                      >
                        👎
                      </button>
                    </div>
                  )}
                </div>
              </li>
            )
          })}
        </ul>

        {conversationId === undefined && (
          <div className="kc3-connecting">
            <div className="kc3-connecting__avatar" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span className="kc3-connecting__avatar-status" />
            </div>
            <div className="kc3-connecting__bubble">
              <span className="kc3-connecting__dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
              Connecting you to a live agent…
            </div>
          </div>
        )}

        {isAgentTyping && (
          <div className="kc3-typing" aria-live="polite">
            <span className="kc3-typing__dots">
              <span />
              <span />
              <span />
            </span>
            <span className="kc3-typing__text">{typingName ? `${typingName} is typing…` : 'Agent is typing…'}</span>
          </div>
        )}
      </div>

      {showEmoji && (
        <Suspense fallback={null}>
          <EmojiPickerWithData onSelect={(native) => setText((t) => t + native)} />
        </Suspense>
      )}

      {isUploading && (
        <div className="kc3-pending-attachment kc3-pending-attachment--uploading">
          <span className="kc3-upload-spinner" aria-hidden="true" />
          <span>Uploading…</span>
        </div>
      )}
      {uploadError && (
        <div className="kc3-pending-attachment kc3-pending-attachment--error">
          <span>{uploadError}</span>
          <button type="button" onClick={() => setUploadError(null)} aria-label="dismiss error">×</button>
        </div>
      )}
      {pendingAttachment && (
        <div className="kc3-pending-attachment">
          <span>{pendingAttachment.filename} ({(pendingAttachment.size / 1024).toFixed(1)} KB)</span>
          <button type="button" onClick={() => setPendingAttachment(null)} aria-label="remove attachment">×</button>
        </div>
      )}
      {noticeGate && privacyNotice && (
        <div className="kc3-privacy-notice" role="group" aria-label="privacy notice">
          {privacyNotice.text && <p className="kc3-privacy-notice__text">{privacyNotice.text}</p>}
          {privacyNotice.url && (
            <a href={privacyNotice.url} target="_blank" rel="noopener noreferrer" className="kc3-privacy-notice__link">
              Privacy notice
            </a>
          )}
          {privacyNotice.minimumAge ? (
            <label className="kc3-privacy-notice__age">
              <input type="checkbox" checked={ageConfirmed} onChange={(e) => setAgeConfirmed(e.target.checked)} />
              {` I confirm I am at least ${privacyNotice.minimumAge} years old`}
            </label>
          ) : null}
          {noticeError && <p className="kc3-privacy-notice__error" role="alert">{noticeError}</p>}
          <button
            type="button"
            className="kc3-privacy-notice__accept"
            onClick={() => void acceptNotice()}
            disabled={noticeBusy || (!!privacyNotice.minimumAge && !ageConfirmed)}
          >
            I understand, continue
          </button>
        </div>
      )}
      {readOnly ? (
        <div className="kc3-composer kc3-composer--ended" role="form" aria-label="chat ended">
          <span>Chat ended.</span>
          {onRestart ? (
            <button type="button" className="kc3-composer-send-text" onClick={onRestart}>
              Start a new chat
            </button>
          ) : null}
        </div>
      ) : (
        <div className="kc3-composer" role="form" aria-label="composer">
          <div className="kc3-composer-input">
            <textarea
              placeholder={
                !canSendMessage
                  ? 'Waiting for an agent to join…'
                  : pendingAttachment
                    ? 'Add a message (optional)…'
                    : 'Type a message…'
              }
              value={text}
              onChange={(e) => {
                setText(e.target.value)
                const typingPayload = conversationId !== undefined ? { conversationId } : {}
                socket.emit('widget:typing:start', typingPayload)
                if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
                typingTimerRef.current = setTimeout(() => {
                  socket.emit('widget:typing:stop', typingPayload)
                }, 1500)
              }}
              onKeyDown={onKeyDown}
              rows={1}
              aria-label="message input"
              disabled={!canSendMessage}
            />
            <button
              type="button"
              onClick={() => setShowEmoji((s) => !s)}
              aria-label="emoji"
              aria-pressed={showEmoji}
              className="kc3-composer-action"
            >
              <SmileIcon />
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              aria-label="attach"
              disabled={isUploading}
              className="kc3-composer-action kc3-composer-action--attach"
            >
              <PaperclipIcon width={18} height={18} />
            </button>
            <input
              type="file"
              ref={fileRef}
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0]
                e.target.value = ''
                if (file) void uploadFile(file)
              }}
            />
          </div>
          <button
            type="button"
            onClick={send}
            aria-label="send"
            className="kc3-composer-send"
            disabled={!canSendMessage || (!text.trim() && !pendingAttachment) || isSending || isUploading}
          >
            <SendIcon className="kc3-composer-icon--send" width={20} height={20} />
          </button>
        </div>
      )}

      <div className="kc3-widget-footer">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        <span>Your data is secure and private</span>
      </div>
    </div>
  )
}

function EmojiPickerWithData({ onSelect }: { onSelect: (native: string) => void }) {
  const [data, setData] = useState<unknown>(null)

  useEffect(() => {
    async function load() {
      // @ts-ignore — emoji-mart data bundle has no ambient types
      const mod = await import('@emoji-mart/data/sets/14/native')
      setData(mod.default)
    }
    void load()
  }, [])

  return (
    <Suspense fallback={null}>
      <EmojiPicker data={data} onEmojiSelect={(e) => onSelect(e.native)} />
    </Suspense>
  )
}

/**
 * Wire shape of the `widget:bot:reply` socket event, relayed 1:1 from
 * `WidgetBotTurnReplyEvent` (packages/contracts/src/events/widget.ts) by
 * ws-gateway's widget-bridge consumer. Defined locally rather than
 * imported from @kc3/contracts because @kc3/chat-widget is a standalone
 * browser bundle with no dependency on the contracts package (it ships
 * to visitor browsers, not through the monorepo's server build). Only
 * published for non-escalating bot turns, so there is no `escalate`
 * field on this event — escalation is only ever observable client-side
 * via a later `widget:message:new` event (see onConversationMessage
 * below), never signaled directly on the bot-reply event itself.
 */
interface WidgetBotReplyPayload {
  message: string
  options?: BotOption[]
}

/**
 * Minimal shape this component reads off `widget:message:new`, matching
 * the real `ConversationMessageNewEvent` (packages/contracts/src/events/
 * conversation.ts) fields used here. The full event carries more fields
 * (messageId, channel, lastMessagePreview, etc.) that Widget/this
 * component don't need to inspect.
 */
interface ConversationMessageNewPayload {
  direction: 'inbound' | 'outbound'
  conversationId: string
  messageId: string
  body?: string
  attachments?: { id: string; filename: string; mimeType: string; sizeBytes: number }[]
  lastMessagePreview: string
  lastMessageAt: string
  /** AG-05: AI-authored message while the tenant's AI disclosure is on. */
  aiGenerated?: boolean
}

/**
 * Must match packages/conversations/src/bots/faq-bot-service.ts's
 * TALK_TO_AGENT_OPTION_ID server-side constant exactly (both
 * DecisionTreeBotService and FaqBotService reuse the same sentinel id for
 * their "Talk to an Agent" menu option). Duplicated here rather than
 * imported since this package has no dependency on @kc3/conversations
 * (see this file's existing WidgetBotReplyPayload comment for why).
 */
const TALK_TO_AGENT_OPTION_ID = '__talk_to_agent__'

/**
 * Mirrors faq-bot-service.ts's START_SENTINEL — the option id the widget
 * sends on a fresh open, which resets the bot session server-side (see
 * apps/worker/src/bots/bot-orchestrator.ts's handleVisitorMessage).
 */
const START_OPTION_ID = '__start__'

// 65s (not the originally-planned 30s): each agent-offer attempt on the
// worker side now waits up to 30s (apps/worker/src/bots/bot-orchestrator.ts's
// offerTimeoutMs — widened from 15s to leave real margin against the
// outbox-processor's up-to-5s poll delay before an Accept reaches the
// worker). This constant is deliberately sized to cover roughly 2 offer
// attempts (30s + 30s) plus a small buffer, so the visitor doesn't see
// "all agents are busy" while a real agent is still deciding on the
// first offer. If the worker's offerTimeoutMs changes, this must too.
const ESCALATION_TIMEOUT_MS = 65_000

/**
 * Module-level guard against React StrictMode double-invocation of the
 * auto-pick effect. A useRef resets on the StrictMode remount (new
 * component instance), so it cannot prevent the effect from firing twice
 * in development — which sends `opt:__talk_to_agent__` twice, triggering
 * two escalation attempts. The first succeeds (agent accepts), the second
 * finds no available agents and sends "All agents are currently busy."
 * A module-level Set persists across the remount, so the second invocation
 * sees the visitorId already present and bails out.
 */
const autoPickedVisitorIds = new Set<string>()

/**
 * Wire shape of the `widget:agent:assigned` socket event, relayed 1:1 from
 * WidgetEscalationAssignedEvent (packages/contracts/src/events/widget.ts)
 * by ws-gateway's widget-bridge consumer. Fires once, right when a human
 * agent is actually assigned to the conversation — before that agent has
 * sent any message — distinct from the message-based AGENT_JOINED signal
 * onConversationMessage already derives from widget:message:new.
 */
interface WidgetAgentAssignedPayload {
  sessionId: string
}

function getInitialConversationId(visitorId: string): string | null {
  try {
    const saved = localStorage.getItem(`kc3-widget-conversation:${visitorId}`)
    return saved ? saved : null
  } catch {
    return null
  }
}

function getInitialWidgetState(visitorId: string, initialConversationId?: string | null): WidgetState {
  // For logged-in clients the host passes the conversationId directly from
  // the API response — no localStorage lookup needed. For anonymous/guest
  // visitors we fall back to the localStorage mapping (backward compatible).
  const conversationId = initialConversationId ?? getInitialConversationId(visitorId)
  return { ...initialWidgetState, conversationId }
}

export function ChatWidgetRoot({
  socket,
  visitorId,
  token,
  onClose,
  onRestart,
  showRating = false,
  ratingScale = 5,
  tenantSlug,
  initialOption = START_OPTION_ID,
  initialNotice,
  initialConversationId,
  persistHistory = true,
  privacyNotice,
}: {
  /** PV-16: tenant-supplied privacy notice to show and record acceptance of; omit when disabled. */
  privacyNotice?: PrivacyNoticeConfig
  socket: Socket
  visitorId: string
  token: string
  onClose?: () => void
  onRestart?: () => void
  showRating?: boolean
  ratingScale?: number
  tenantSlug?: string
  /**
   * Option id sent as the first bot turn when the widget opens without an
   * existing conversation. Defaults to the `__start__` sentinel (greeting
   * menu). Hosts that already know the visitor asked for a human — e.g.
   * apps/web's ClientChatLauncher, which only mounts this component after
   * its AI chat's "Talk to a human" action — pass
   * TALK_TO_AGENT_OPTION_ID so the bot escalates straight away instead of
   * re-showing the menu the visitor just answered. A non-default value also
   * auto-selects that option on mount (skipping the greeting menu) — see
   * the auto-pick effect below.
   */
  initialOption?: string
  /**
   * One-time informational notice shown as the first bot message when the
   * widget opens — e.g. "Your previous agent is no longer available.
   * Connecting you with a new available agent…" when the portal abandoned
   * a stale chat and is re-escalating to a different agent. Only shown
   * when there is no existing conversationId (fresh escalation).
   */
  initialNotice?: string
  /**
   * The live-chat conversation id from the API response (for logged-in
   * clients). When provided, the widget skips the localStorage conversation
   * mapping lookup and uses this value directly — the database is the source
   * of truth, not the browser.
   */
  initialConversationId?: string
  /**
   * When false (logged-in clients), messages are not cached in localStorage.
   * The widget fetches history from the database via the
   * `widget:history:request` Socket.IO event on mount. Defaults to true for
   * backward compatibility with anonymous/guest visitors.
   */
  persistHistory?: boolean
}) {
  const [state, dispatch] = useReducer(
    widgetReducer,
    { visitorId, initialConversationId },
    ({ visitorId: vid, initialConversationId: initCid }: { visitorId: string; initialConversationId?: string }) =>
      getInitialWidgetState(vid, initCid),
  )
  const [initialMessage, setInitialMessage] = useState<ConversationMessageNewPayload | null>(null)
  const [isChatEnded, setIsChatEnded] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const prevStatusRef = useRef<WidgetState['status'] | undefined>(undefined)

  useLayoutEffect(() => {
    if (onClose && prevStatusRef.current && prevStatusRef.current !== 'closed' && state.status === 'closed') {
      onClose()
    }
    prevStatusRef.current = state.status
  }, [state.status, onClose])

  useEffect(() => {
    if (state.status === 'closed') return
    const onPointerDown = (e: PointerEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        dispatch({ type: 'CLOSE' })
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [state.status])

  // Clear the auto-picked guard when the widget closes so a manual
  // close-and-reopen can re-send the initial option. Without this, the
  // module-level Set would permanently suppress the initial message for
  // this visitorId after the first auto-pick.
  useEffect(() => {
    if (state.status === 'closed') {
      autoPickedVisitorIds.delete(visitorId)
    }
  }, [state.status, visitorId])

  useEffect(() => {
    if (!persistHistory) return
    try {
      if (state.conversationId) {
        localStorage.setItem(`kc3-widget-conversation:${visitorId}`, state.conversationId)
      } else {
        localStorage.removeItem(`kc3-widget-conversation:${visitorId}`)
      }
    } catch {
      // ignore storage errors (e.g. private mode)
    }
  }, [state.conversationId, visitorId, persistHistory])

  useEffect(() => {
    const onBotReply = (m: WidgetBotReplyPayload) => {
      dispatch({ type: 'BOT_REPLY', message: m.message, ...(m.options !== undefined && { options: m.options }) })
      if (m.message === 'Connecting you to a live agent…') {
        dispatch({ type: 'ESCALATE' })
      }
    }
    const onConversationMessage = (e: ConversationMessageNewPayload) => {
      if (e.direction === 'outbound') {
        dispatch({ type: 'AGENT_JOINED', conversationId: e.conversationId })
        setInitialMessage(e)
      }
    }
    const onAgentAssigned = (e: WidgetAgentAssignedPayload) => {
      dispatch({ type: 'AGENT_JOINED', conversationId: e.sessionId })
    }
    const onConversationEnded = () => {
      dispatch({ type: 'CONVERSATION_ENDED' })
      clearWidgetStorage(visitorId)
    }
    const onChatEnded = () => {
      setIsChatEnded(true)
      // The live-chat end event is distinct from conversation.closed, but
      // it must move the widget into the ended screen so the client can
      // start a fresh portal session instead of remaining in a read-only
      // composer bound to the retired live_chats row.
      dispatch({ type: 'CONVERSATION_ENDED' })
      clearWidgetStorage(visitorId)
    }
    socket.on('widget:bot:reply', onBotReply)
    socket.on('widget:message:new', onConversationMessage)
    socket.on('widget:agent:assigned', onAgentAssigned)
    socket.on('widget:conversation:ended', onConversationEnded)
    socket.on('widget:chat:ended', onChatEnded)
    return () => {
      socket.off('widget:bot:reply', onBotReply)
      socket.off('widget:message:new', onConversationMessage)
      socket.off('widget:agent:assigned', onAgentAssigned)
      socket.off('widget:conversation:ended', onConversationEnded)
      socket.off('widget:chat:ended', onChatEnded)
    }
  }, [socket, onRestart])

  useEffect(() => {
    if (state.status !== 'connecting') return
    const timer = setTimeout(() => {
      dispatch({ type: 'ESCALATION_TIMEOUT' })
    }, ESCALATION_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [state.status])

  function openWidget() {
    dispatch({ type: 'OPEN' })
    if (!state.conversationId && !autoPickedVisitorIds.has(visitorId)) {
      autoPickedVisitorIds.add(visitorId)
      socket.emit('widget:message:send', { content: `opt:${initialOption}` })
    }
  }

  // Lets host-page markup outside this component (e.g. a "Live Chat" link in
  // a portal footer, which is raw HTML rendered via dangerouslySetInnerHTML
  // and has no direct access to this component's props/state) open the
  // widget by dispatching `window.dispatchEvent(new CustomEvent('kc3:open-chat'))`.
  // Registered once; reads state.status/openWidget via a ref so the listener
  // itself never needs to be torn down and re-added on every state change.
  const openWidgetRef = useRef(openWidget)
  openWidgetRef.current = openWidget
  const widgetStatusRef = useRef(state.status)
  widgetStatusRef.current = state.status

  useEffect(() => {
    const onOpenChatEvent = () => {
      if (widgetStatusRef.current === 'closed') openWidgetRef.current()
    }
    window.addEventListener('kc3:open-chat', onOpenChatEvent)
    return () => window.removeEventListener('kc3:open-chat', onOpenChatEvent)
  }, [])

  function pickOption(optionId: string, label: string) {
    dispatch({ type: 'OPTION_PICKED', label })
    socket.emit('widget:message:send', { content: `opt:${optionId}` })
  }

  // Auto-open straight into a specific bot-menu option (e.g. "Talk to an
  // Agent") for callers that already know what the visitor wants, skipping
  // the greeting menu the manual openWidget() path shows. Gated on a
  // non-default initialOption — the default (START_OPTION_ID) means "no
  // specific ask," so it must not auto-open the widget on every mount. Only
  // on first mount, and only when there's no saved conversation to resume
  // instead — the module-level Set guard keeps this from re-firing on
  // StrictMode's remount (a useRef resets on remount, so it can't prevent
  // the double-invocation).
  useEffect(() => {
    if (autoPickedVisitorIds.has(visitorId)) return
    if (!initialOption || initialOption === START_OPTION_ID || state.conversationId) return
    autoPickedVisitorIds.add(visitorId)
    dispatch({ type: 'OPEN' })
    if (initialNotice) {
      dispatch({ type: 'BOT_REPLY', message: initialNotice })
    }
    pickOption(initialOption, initialOption === TALK_TO_AGENT_OPTION_ID ? 'Talk to an Agent' : initialOption)
  }, [])

  if (state.status === 'closed') {
    return (
      <button type="button" className="kc3-widget-bubble" onClick={openWidget} aria-label="Open chat">
        <span className="kc3-widget-bubble__icon" aria-hidden="true">💬</span>
      </button>
    )
  }

  if (state.status === 'connecting') {
    return (
      <div className="kc3-widget-panel" ref={panelRef}>
        <button type="button" className="kc3-widget-close" onClick={() => dispatch({ type: 'CLOSE' })} aria-label="Close chat">
          ✕
        </button>
        <div className="kc3-widget kc3-widget--connecting">
          <div className="kc3-widget-header">
            <div className="kc3-widget-header__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div className="kc3-widget-header__content">
              <span className="kc3-widget-header__title">Live Agent</span>
              <span className="kc3-widget-header__subtitle">
                <span className="kc3-widget-header__online" />
                Online
              </span>
            </div>
          </div>

          <div className="kc3-messages-scroll">
            <div className="kc3-messages-divider">
              <span className="kc3-messages-divider__line" />
              <span className="kc3-messages-divider__text">Today</span>
              <span className="kc3-messages-divider__line" />
            </div>
            <ul className="kc3-messages" aria-label="conversation" aria-live="polite" aria-relevant="additions">
              {state.history.map((turn, i) => (
                <li key={i} className={`kc3-message kc3-message--${turn.from === 'bot' ? 'agent' : 'visitor'}`}>
                  {turn.from === 'bot' && (
                    <div className="kc3-message__avatar" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      <span className="kc3-message__avatar-status" />
                    </div>
                  )}
                  <div className="kc3-message__bubble">
                    <span className="kc3-message__text">{turn.text}</span>
                  </div>
                </li>
              ))}
            </ul>

            <div className="kc3-connecting">
              <div className="kc3-connecting__avatar" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span className="kc3-connecting__avatar-status" />
              </div>
              <div className="kc3-connecting__bubble">
                <span className="kc3-connecting__dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
                Connecting you to a live agent…
              </div>
            </div>
          </div>

          <div className="kc3-widget-footer">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>Your data is secure and private</span>
          </div>
        </div>
      </div>
    )
  }

  if (state.status === 'escalation_failed') {
    return (
      <div className="kc3-widget-panel" ref={panelRef}>
        <button type="button" className="kc3-widget-close" onClick={() => dispatch({ type: 'CLOSE' })} aria-label="Close chat">
          ✕
        </button>
        <AgentsBusy
          onRetry={() => {
            dispatch({ type: 'RETRY_ESCALATION' })
            socket.emit('widget:message:send', { content: `opt:${TALK_TO_AGENT_OPTION_ID}` })
          }}
          onClose={() => dispatch({ type: 'CLOSE' })}
        />
      </div>
    )
  }

  if (state.status === 'live_chat') {
    return (
      <div className="kc3-widget-panel" ref={panelRef}>
        <button
          type="button"
          className="kc3-widget-close"
          onClick={() => dispatch({ type: 'CONVERSATION_ENDED' })}
          aria-label="Close chat"
        >
          ✕
        </button>
        <Widget
          socket={socket}
          visitorId={visitorId}
          token={token}
          persistHistory={persistHistory}
          readOnly={isChatEnded}
          onRestart={onRestart}
          {...(privacyNotice && { privacyNotice })}
          {...(state.conversationId != null && { conversationId: state.conversationId })}
          {...(initialMessage != null && { initialMessage })}
        />
      </div>
    )
  }

  if (state.status === 'ended') {
    return (
      <div className="kc3-widget-panel" ref={panelRef}>
        <button type="button" className="kc3-widget-close" onClick={() => dispatch({ type: 'CLOSE' })} aria-label="Close chat">
          ✕
        </button>
        <div className="kc3-widget kc3-widget--ended">
          {showRating && tenantSlug ? (
            <RatingPopup
              scale={ratingScale}
              {...(state.conversationId != null && { conversationId: state.conversationId })}
              tenantSlug={tenantSlug}
              authToken={token}
              onSubmitted={() => dispatch({ type: 'CLOSE' })}
              onDismiss={() => dispatch({ type: 'CLOSE' })}
            />
          ) : (
            <div className="kc3-widget-ended__content">
              <p>Chat ended.</p>
              {onRestart ? (
                <button
                  type="button"
                  className="kc3-composer-send-text"
                  onClick={onRestart}
                >
                  Start a new chat
                </button>
              ) : null}
            </div>
          )}
        </div>
      </div>
    )
  }
}
